/* eslint-disable no-useless-return */
import React, { BaseSyntheticEvent, useEffect, useState } from 'react';
import TextInput from 'components/input/text';
import FormulaInput from 'components/formula-entry/formula';
import MarkerSearchBox from 'components/formula-entry/marker-search-box';
import MarkerSearchResults from 'components/formula-entry/marker-search-results';
import { IFormulaEntryState, IMarker, IMarkerList } from 'store/formula-entry/types';
import { FormulasInterface, IDeal } from 'store/deals/types';
import { ModalContainerDark, OptionModal } from 'components/modal-provider/modal';
import {
  ButtonContainer,
  CancelButton,
  CloseIcon,
  Container,
  ContentSection,
  ContentSectionPanel,
  ContentSeparator,
  ContentWithScroll,
  DoubleArrowIcon,
  FormControlLabel,
  InformationPanel,
  InputError,
  InputGroup,
  InputGroupRow,
  Label,
  EditLabel,
  OptionSectionPanel,
  SaveFormulaButton,
  Title,
  TitleContainer,
  Radio,
  RadioGroup,
  VolumeOptionModalSpacing,
} from 'components/formula-entry/modals/_common';
import { EOptionOwner, IVolumeOption, ECycle, IPrice, IDealProduct } from 'store/field-options/types';
import { handleError } from 'lib/helpers/error-handler';
import messages from 'constants/messages';
import { EInputField, getOptionInputError, getOptionCountErrors } from 'lib/validators/option';
import Select from 'components/input/select';
import MenuItem from 'components/menu-item';
import FormControl from 'components/input/form-control';
import { isMatch } from 'store/formula-entry/match';
import {
  validateFormula,
  getUnitLabel,
  triggerDuplicateMarkerPreventionTimer,
} from 'components/formula-entry/modals/helpers';
import { capitalizeFirstLetter } from 'lib/helpers/string-manipulators';
import optionErrors from 'lib/validators/option.errors';
import { calculateNumOfMonths } from 'lib/helpers/date-range';
import { ELoadingType } from 'store/loading/types';
import MarkerFilters from '../formula-list/marker-filters';
import { IErrors, TConcatArraysReturnType, TVolumeParsed } from '../types';

interface IFormulaEntryContainerProps {
  cancelFormulaEdit: () => void;
  dealEdit: IDeal;
  formulaEntry: IFormulaEntryState;
  getDealEvaluation: () => void;
  setLoading: (loading: ELoadingType) => void;
  updateProductField: Function;
  allTypesMarkerList: TConcatArraysReturnType[];
  updateMarkerList: Function;
}

const errorsInitialState: IErrors = {
  volume: null,
  count: null,
  notice: null,
  shellBatna: null,
  custBatna: null,
  basePrice: null,
};

const checkProductIndex = (productIndex: number | null) => {
  return productIndex === null;
};

const trimVolumeOptionShellBatna = (volumeOption: IVolumeOption) => {
  return volumeOption.shellBatna ? volumeOption.shellBatna.trim() : '';
};

const trimVolumeOptionCustBatna = (volumeOption: IVolumeOption) => {
  return volumeOption.custBatna ? volumeOption.custBatna.trim() : '';
};

const checkVolumnTypeValue = (volumeOption: IVolumeOption) => {
  return volumeOption.volume && typeof volumeOption.volume === 'string';
};

const validateVolumeParsedValue = (volumeParsed: TVolumeParsed) => {
  return volumeParsed === '' || volumeParsed === 0 || volumeParsed === '0';
};

const validateVolumeParsedValueUndefinedOrNumber = (volumeParsed: TVolumeParsed) => {
  return volumeParsed === undefined || Number.isNaN(volumeParsed);
};

const checkVolumeParsednullAndUndefinedCheck = (volumeParsednullCheck: boolean, volumeParsedUndefineCheck: boolean) => {
  return volumeParsednullCheck || volumeParsedUndefineCheck;
};

const checkVolumeOptionOwnerAndcustBatna = (volumeOption: IVolumeOption) => {
  return volumeOption.owner === EOptionOwner.CUSTOMER && volumeOption.custBatna;
};

const checkVolumeOptionCustBatna = (volumeOption: IVolumeOption) => {
  return volumeOption.owner === EOptionOwner.CUSTOMER && !volumeOption.custBatna;
};

const checkNewErrorVolumeCountAndNotice = (newErrors: IErrors) => {
  return !newErrors.volume && !newErrors.count && !newErrors.notice;
};

const checkNewErrorShellAndCustBatna = (newErrors: IErrors) => {
  return !newErrors.shellBatna && !newErrors.custBatna;
};

const checkNewErrorVolumnAndBatna = (newErrorsVolumCheck: boolean, newErrorsBatnaCheck: boolean) => {
  return newErrorsVolumCheck && newErrorsBatnaCheck;
};

async function processSave(
  productIndex: number | null,
  volumeOption: IVolumeOption,
  updateProductField: Function,
  getDealEvaluation: { (): void; (): void },
  cancelFormulaEdit: { (): void; (): void },
  setLoading: { (loading: ELoadingType): void; (arg0: ELoadingType): void },
  {
    dealFormulas,
    setErrors,
    dealEdit,
    numberOfMonths,
  }: {
    dealFormulas: FormulasInterface[] | undefined;
    setErrors: Function;
    dealEdit: IDeal;
    numberOfMonths: number | undefined;
  }
) {
  const volumeOptionReassin = volumeOption;
  if (checkProductIndex(productIndex)) {
    handleError(messages.errors.default);
    return;
  }

  // Trim input values
  volumeOptionReassin.shellBatna = trimVolumeOptionShellBatna(volumeOption);
  volumeOptionReassin.custBatna = trimVolumeOptionCustBatna(volumeOption);
  if (checkVolumnTypeValue(volumeOption)) {
    // @ts-ignore
    volumeOptionReassin.volume = volumeOption.volume.trim();
  }

  // Check for empty volume -- delete option scenario
  const volumeParsed: TVolumeParsed = volumeOption.volume;
  const volumeParsednullCheck = validateVolumeParsedValue(volumeParsed);
  const volumeParsedUndefineCheck = validateVolumeParsedValueUndefinedOrNumber(volumeParsed);
  if (checkVolumeParsednullAndUndefinedCheck(volumeParsednullCheck, volumeParsedUndefineCheck)) {
    updateProductField(productIndex, 'pull', null);
    getDealEvaluation();
    cancelFormulaEdit();
    return;
  }

  // API calls
  setLoading(ELoadingType.SPINNER);

  // Initialize new error object
  const newErrors = { ...errorsInitialState };

  try {
    // Shell BATNA
    if (volumeOption.shellBatna) {
      const { shellBatna: formula } = volumeOption;
      const shellBatnaErrorResponse = await validateFormula(dealFormulas, [{ formula, name: '' }]);
      const shellBatnaError = shellBatnaErrorResponse.data.error;
      if (shellBatnaError) {
        newErrors.shellBatna = capitalizeFirstLetter(shellBatnaError.message);
      }
    } else {
      newErrors.shellBatna = optionErrors.requiredField;
    }

    // Customer BATNA
    if (checkVolumeOptionOwnerAndcustBatna(volumeOption)) {
      const { custBatna: formula } = volumeOption;
      const custBatnaErrorResponse = await validateFormula(dealFormulas, [{ formula, name: '' }]);
      const custBatnaError = custBatnaErrorResponse.data.error;
      if (custBatnaError) {
        newErrors.custBatna = capitalizeFirstLetter(custBatnaError.message);
      }
    } else if (checkVolumeOptionCustBatna(volumeOption)) {
      newErrors.custBatna = optionErrors.requiredField;
    }
  } catch (e) {
    handleError(e);
    setLoading(ELoadingType.NONE);
  }

  // Parse volume
  if (volumeOption?.volume) {
    const volumeValue: number = volumeOption.volume;
    const volumeConverted: number = parseFloat(volumeValue.toString());
    volumeOptionReassin.volume = volumeConverted;
  }

  // Validate volume
  newErrors.volume = getOptionInputError(EInputField.VOLUME, volumeOption.volume);

  // Validate option count
  newErrors.count = getOptionCountErrors(volumeOption.cycle, volumeOption.count, numberOfMonths);

  // Validate notification period
  newErrors.notice = getOptionInputError(EInputField.NOTICE, volumeOption.notice);

  const newErrorsVolumCheck = checkNewErrorVolumeCountAndNotice(newErrors);
  const newErrorsBatnaCheck = checkNewErrorShellAndCustBatna(newErrors);

  // Update deal, re-evaluate, and close modal if no errors exist
  if (checkNewErrorVolumnAndBatna(newErrorsVolumCheck, newErrorsBatnaCheck)) {
    updateProductField(productIndex, 'pull', volumeOption);
    getDealEvaluation();
    cancelFormulaEdit();
  } else {
    setErrors(newErrors);
    setLoading(ELoadingType.NONE);
  }
}

const volumeOptionInitialState: IVolumeOption = {
  enabled: true,
  owner: EOptionOwner.SHELL,
  volume: null,
  count: 1,
  cycle: ECycle.ANNUAL,
  notice: 15,
  basePrice: '',
  shellBatna: '',
  custBatna: '',
};

enum EFormula {
  NONE,
  SHELL_BATNA,
  CUSTOMER_BATNA,
}

function EditText() {
  return <EditLabel>Default value BASE & BATNA has been changed</EditLabel>;
}

const checkVolumeValueAndUndefined = (e: React.ChangeEvent<HTMLInputElement>) => {
  return parseFloat(e.target.value) || undefined;
};

const checkVolumeOptionShellBatnaMainBatnaCopy = (volumeOption: IVolumeOption, mainBatnaCopy: string) => {
  return (
    (volumeOption.shellBatna !== mainBatnaCopy || (volumeOption.shellBatna === '' && mainBatnaCopy !== '')) && (
      <EditText />
    )
  );
};

const checkActiveFormulaForShellBatna = (activeFormula: EFormula) => {
  return activeFormula === EFormula.SHELL_BATNA;
};

const checkActiveFormulaForCustomerBatna = (activeFormula: EFormula) => {
  return activeFormula === EFormula.CUSTOMER_BATNA;
};

const getVolumeOptionVolume = (volumeOption: IVolumeOption) => {
  return volumeOption.volume ?? '';
};

const getVolumeOptionCount = (volumeOption: IVolumeOption) => {
  return volumeOption.count ?? '';
};

const getVolumeOptionNotice = (volumeOption: IVolumeOption) => {
  return volumeOption.notice ?? '';
};

const getVolumeOptionShellBatna = (volumeOption: IVolumeOption) => {
  return volumeOption.shellBatna ?? '';
};

const checkVolumeOptionOwner = (volumeOption: IVolumeOption) => {
  return volumeOption.owner === EOptionOwner.CUSTOMER;
};

const getVolumeOptionCustBatna = (volumeOption: IVolumeOption) => {
  return volumeOption.custBatna ?? '';
};

const getVolumeUnitLabel = (volumeUnit: string | undefined) => {
  return volumeUnit ? `(${volumeUnit})` : '';
};

const getPriceValueFormula = (basePrice: IPrice | undefined) => {
  return basePrice ? basePrice.formula : '';
};

const getMarkers = (markerList: IMarkerList[], allTypesMarkerList: TConcatArraysReturnType[]) => {
  return markerList || allTypesMarkerList;
};

const getFormula = (mainBatna: IPrice | undefined) => {
  return mainBatna ? mainBatna.formula : '';
};

const getVolumeUnitLabelAndpriceValueFormulaValue = (
  products: IDealProduct[] | undefined,
  productIndex: number | null
) => {
  let volumeUnitLabel = '';
  let priceValueFormula = '';
  if (products && productIndex !== null) {
    const { volumeUnit, basePrice } = products[productIndex];
    volumeUnitLabel = getVolumeUnitLabel(volumeUnit);
    priceValueFormula = getPriceValueFormula(basePrice);
  }
  return { volumeUnitLabel, priceValueFormula };
};

const processFormulaValueForShellBatna = (volumeOption: IVolumeOption, cursorPosition: number, name: string) => {
  const formula = getVolumeOptionShellBatna(volumeOption);
  return [formula.slice(0, cursorPosition), `[${name}]`, formula.slice(cursorPosition)].join('');
};

const processFormulaValueForCustBatna = (volumeOption: IVolumeOption, cursorPosition: number, name: string) => {
  const formula = getVolumeOptionCustBatna(volumeOption);
  return [formula.slice(0, cursorPosition), `[${name}]`, formula.slice(cursorPosition)].join('');
};

const returnHandleError = (availableMarkers: IMarker[] | null) => {
  if (!availableMarkers) {
    handleError(messages.errors.noMarkers);
  }
};

const checkTimer = (timer: string | null) => {
  // Check for active timer, if found, prevent insertion of duplicate marker
  if (timer) {
    return;
  }
};
const getMainBatnaAndPullValue = (
  isInitialized: boolean,
  products: IDealProduct[] | undefined,
  productIndex: number | null
) => {
  if (!isInitialized && products && productIndex !== null) {
    const { mainBatna, pull } = products[productIndex];
    return { mainBatna, pull };
  }
  return null;
};

const checkIsInitializedAndProductsValue = (
  isInitialized: boolean,
  products: IDealProduct[] | undefined,
  productIndex: number | null
) => {
  return !isInitialized && products && productIndex !== null;
};

export default ({
  cancelFormulaEdit,
  dealEdit,
  formulaEntry,
  getDealEvaluation,
  setLoading,
  updateProductField,
  allTypesMarkerList,
  updateMarkerList,
}: IFormulaEntryContainerProps) => {
  const { availableMarkers, productIndex, markerList } = formulaEntry;
  const { products, formulas: dealFormulas } = dealEdit;
  const [mainBatnaCopy, setMainBatnaCopy] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [shellBatnaEdit, setShellBatnaEdit] = useState(false);
  const [volumeOption, setVolumeOption] = useState(volumeOptionInitialState);
  const [errors, setErrors] = useState(errorsInitialState);

  const [activeFormula, setActiveFormula] = useState(EFormula.SHELL_BATNA);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [filteredMarkers, setFilteredMarkers] = useState(availableMarkers);
  const [markerSearchValue, setMarkerSearchValue] = useState('');
  const [timer, setTimer] = useState(null);

  useEffect(() => {
    setFilteredMarkers(markerList);
  }, [markerList]);

  // Build dynamic labels and base price formula
  const pricePerVolumeUnitLabel = getUnitLabel(products, productIndex);
  const { volumeUnitLabel, priceValueFormula } = getVolumeUnitLabelAndpriceValueFormulaValue(products, productIndex);

  // Calculate length of contract (months) for validation of option exercise count
  const numberOfMonths = calculateNumOfMonths(products);

  // Inject marker at cursorPosition
  function insertMarker(name: string) {
    // Check for active timer, if found, prevent insertion of duplicate marker
    checkTimer(timer);

    // Kickoff new timer to handle double-clicking of markers
    triggerDuplicateMarkerPreventionTimer(setTimer);

    // Determine which formula is being edited
    if (checkActiveFormulaForShellBatna(activeFormula)) {
      setVolumeOption({
        ...volumeOption,
        shellBatna: processFormulaValueForShellBatna(volumeOption, cursorPosition, name),
      });
    } else if (checkActiveFormulaForCustomerBatna(activeFormula)) {
      setVolumeOption({
        ...volumeOption,
        custBatna: processFormulaValueForCustBatna(volumeOption, cursorPosition, name),
      });
    }
  }

  // Manage filtered marker list
  useEffect(() => {
    returnHandleError(availableMarkers);
    const markers = getMarkers(markerList, allTypesMarkerList);
    const filteredMarkersNew = markers.filter((marker: IMarker) => isMatch(markerSearchValue, marker.name));
    setFilteredMarkers(filteredMarkersNew);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableMarkers, markerList, markerSearchValue]);

  // Update marker value and filtered list
  function handleSearchValueChange(e: React.ChangeEvent<HTMLInputElement>) {
    returnHandleError(availableMarkers);
    const val = e.target.value;
    setMarkerSearchValue(val);
  }

  // Handle clear button for marker search input
  function handleSearchValueClear() {
    setMarkerSearchValue('');
    setFilteredMarkers(availableMarkers);
  }

  // Update formulas in product
  async function save() {
    processSave(productIndex, volumeOption, updateProductField, getDealEvaluation, cancelFormulaEdit, setLoading, {
      dealFormulas,
      setErrors,
      dealEdit,
      numberOfMonths,
    });
  }

  useEffect(() => {
    // Initialize fields
    if (checkIsInitializedAndProductsValue(isInitialized, products, productIndex)) {
      setIsInitialized(true);
      const batnaAndPullValue = getMainBatnaAndPullValue(isInitialized, products, productIndex);
      const pull = batnaAndPullValue?.pull;
      const mainBatna = batnaAndPullValue?.mainBatna;
      if (pull) {
        setVolumeOption(pull);
      } else {
        setVolumeOption({
          ...volumeOption,
          shellBatna: getFormula(mainBatna),
          custBatna: getFormula(mainBatna),
        });
      }
      if (mainBatna) {
        setMainBatnaCopy(mainBatna.formula);
      }
    }
  }, [volumeOption, isInitialized, productIndex, products]);

  function handleOwnerToggle(e: BaseSyntheticEvent) {
    setVolumeOption({
      ...volumeOption,
      owner: e.target.value,
    });
  }

  return (
    <ModalContainerDark>
      <OptionModal>
        <Container>
          <TitleContainer>
            <Title>Pull Option Entry</Title>
            <CloseIcon onClick={cancelFormulaEdit} />
          </TitleContainer>

          <ContentWithScroll>
            <VolumeOptionModalSpacing />
            <ContentSection>
              <OptionSectionPanel>
                <InputGroup>
                  <Label>OWNER</Label>
                  <RadioGroup
                    aria-label="view-type"
                    name="radio-view-type"
                    value={volumeOption.owner}
                    onChange={handleOwnerToggle}
                  >
                    <FormControlLabel value={EOptionOwner.SHELL} control={<Radio />} label="Shell" />
                    <FormControlLabel value={EOptionOwner.CUSTOMER} control={<Radio />} label="Customer" />
                  </RadioGroup>
                  <InputError />
                </InputGroup>

                <InputGroup>
                  <Label data-valid={!!errors.volume}>{`OPTION VOLUME ${volumeUnitLabel.toUpperCase()}`}</Label>
                  <TextInput
                    data-testid="tid-option-volume"
                    value={getVolumeOptionVolume(volumeOption)}
                    onChange={(e: BaseSyntheticEvent) => setVolumeOption({ ...volumeOption, volume: e.target.value })}
                    type="text"
                  />
                  <InputError>{errors.volume}</InputError>
                </InputGroup>

                <InputGroupRow>
                  <InputGroup>
                    <Label data-valid={!!errors.count}>NUMBER OF OPTIONS</Label>
                    <TextInput
                      value={getVolumeOptionCount(volumeOption)}
                      onChange={e => setVolumeOption({ ...volumeOption, count: checkVolumeValueAndUndefined(e) })}
                      type="number"
                    />
                    <InputError>{errors.count}</InputError>
                  </InputGroup>

                  <InputGroup>
                    <Label>CYCLE</Label>
                    <FormControl variant="outlined">
                      <Select
                        value={volumeOption.cycle}
                        onChange={(e: BaseSyntheticEvent) =>
                          setVolumeOption({ ...volumeOption, cycle: e.target.value })
                        }
                      >
                        <MenuItem value={ECycle.CONTRACT}>Contract</MenuItem>
                        <MenuItem value={ECycle.ANNUAL}>Annually</MenuItem>
                        <MenuItem value={ECycle.SEMI_ANNUAL}>Semi-Annually</MenuItem>
                        <MenuItem value={ECycle.QUARTERLY}>Quarterly</MenuItem>
                      </Select>
                    </FormControl>
                    <InputError />
                  </InputGroup>
                </InputGroupRow>

                <InputGroup>
                  <Label data-valid={!!errors.notice}>NOTIFICATION PERIOD (IN DAYS)</Label>
                  <TextInput
                    data-testid="tid-notification-period"
                    value={getVolumeOptionNotice(volumeOption)}
                    onChange={e => setVolumeOption({ ...volumeOption, notice: parseFloat(e.target.value) })}
                    type="number"
                  />
                  <InputError>{errors.notice}</InputError>
                </InputGroup>
              </OptionSectionPanel>
              <InformationPanel>
                A <strong>pull option</strong> allows the option owner to cause Shell to deliver less product to the
                customer.
              </InformationPanel>
            </ContentSection>

            <ContentSeparator>
              <div>
                <DoubleArrowIcon />
                <span>Scroll down to update price formulas</span>
              </div>
            </ContentSeparator>

            <ContentSection>
              <ContentSectionPanel>
                <InputGroup>
                  <Label>{`BASE FORMULA ${pricePerVolumeUnitLabel.toUpperCase()}`}</Label>
                  <FormulaInput formulaInputValue={priceValueFormula} readOnly />
                  <InputError />
                </InputGroup>

                <InputGroup>
                  <Label
                    data-valid={!!errors.shellBatna}
                  >{`SHELL BATNA FORMULA ${pricePerVolumeUnitLabel.toUpperCase()}`}</Label>
                  <FormulaInput
                    formulaInputValue={getVolumeOptionShellBatna(volumeOption)}
                    isActive={checkActiveFormulaForShellBatna(activeFormula)}
                    onChange={e => {
                      setVolumeOption({ ...volumeOption, shellBatna: e.target.value });
                      if (!shellBatnaEdit) {
                        setShellBatnaEdit(true);
                      }
                    }}
                    setActiveFormula={() => setActiveFormula(EFormula.SHELL_BATNA)}
                    setCursorPosition={(p: number) => setCursorPosition(p)}
                    setFormula={(value: string) => setVolumeOption({ ...volumeOption, shellBatna: value })}
                  />
                  {checkVolumeOptionShellBatnaMainBatnaCopy(volumeOption, mainBatnaCopy)}
                  <InputError>{errors.shellBatna}</InputError>
                </InputGroup>

                {checkVolumeOptionOwner(volumeOption) ? (
                  <InputGroup>
                    <Label data-valid={!!errors.custBatna}>{`CUSTOMER BATNA FORMULA ${getUnitLabel(
                      products,
                      productIndex
                    ).toUpperCase()}`}</Label>
                    <FormulaInput
                      formulaInputValue={getVolumeOptionCustBatna(volumeOption)}
                      isActive={checkActiveFormulaForCustomerBatna(activeFormula)}
                      onChange={e => setVolumeOption({ ...volumeOption, custBatna: e.target.value })}
                      setActiveFormula={() => setActiveFormula(EFormula.CUSTOMER_BATNA)}
                      setCursorPosition={(p: number) => setCursorPosition(p)}
                      setFormula={(value: string) => setVolumeOption({ ...volumeOption, custBatna: value })}
                    />
                    <InputError>{errors.custBatna}</InputError>
                  </InputGroup>
                ) : null}
              </ContentSectionPanel>

              <ContentSectionPanel>
                <Label>SEARCH FOR MARKER</Label>
                <MarkerSearchBox
                  value={markerSearchValue}
                  onChange={handleSearchValueChange}
                  onClear={handleSearchValueClear}
                />
                <MarkerFilters allTypesMarkerList={allTypesMarkerList} updateMarkerList={updateMarkerList} />
                <MarkerSearchResults markers={filteredMarkers} onClick={insertMarker} />
              </ContentSectionPanel>
            </ContentSection>
          </ContentWithScroll>

          <ButtonContainer>
            <CancelButton onClick={cancelFormulaEdit}>Cancel</CancelButton>
            <SaveFormulaButton onClick={save}>Apply Option</SaveFormulaButton>
          </ButtonContainer>
        </Container>
      </OptionModal>
    </ModalContainerDark>
  );
};
